/**
 * Schema Converter
 * Converts MCP get_data_resource_schema response to ResourceSchema format
 */

import type { ResourceSchema, ResourceField, GroupField, DropdownOption } from "./types";

/**
 * MCP field schema as returned by get_data_resource_schema
 */
interface MCPField {
  name: string;
  type: string;
  required?: boolean;
  description?: string;
  example?: string | number | boolean;
  default?: unknown;
  sensitive?: boolean;
  allowed_values?: string[];
  nested_schema?: {
    type?: string;
    fields: MCPField[];
    note?: string;
  };
  range?: {
    min: number;
    max: number;
    step?: number;
  };
  // DSL-specific properties
  display_name?: string;
  is_list?: boolean;
  columns?: number;
  field_direction?: "col" | "row";
  collapsible?: boolean;
  collapsed?: boolean;
  show?: boolean;
  editable?: boolean;
  language?: "json" | "python" | "sql";
  multiline?: boolean;
  options?: DropdownOption[] | string[];  // Can be string[] or DropdownOption[]
}

/**
 * MCP schema response
 */
interface MCPSchemaResponse {
  type: string;
  display_name: string;
  description?: string;
  category?: string;
  api_endpoint?: string;
  fields: MCPField[];
  validation_behavior?: {
    timing: string;
    checks: string[];
    connectivity_test: boolean;
  };
  agent_guidance?: {
    required_steps: string[];
    common_errors: string[];
    best_practices: string[];
  };
}

/**
 * Convert MCP schema response to ResourceSchema
 */
export function mcpSchemaToFormSchema(mcpSchema: MCPSchemaResponse): ResourceSchema {
  return {
    type: mcpSchema.type,
    display_name: mcpSchema.display_name,
    description: mcpSchema.description,
    category: mcpSchema.category,
    fields: mcpSchema.fields.map(convertField),
    validation_behavior: mcpSchema.validation_behavior,
    agent_guidance: mcpSchema.agent_guidance,
  };
}

/**
 * Convert a single MCP field to ResourceField
 */
function convertField(mcpField: MCPField): ResourceField {
  const baseField = {
    name: mcpField.name,
    display_name: mcpField.display_name || formatDisplayName(mcpField.name),
    description: mcpField.description,
    required: mcpField.required ?? false,
    placeholder: mcpField.example !== undefined ? String(mcpField.example) : undefined,
    default: mcpField.default,
    show: mcpField.show ?? true,
    editable: mcpField.editable ?? true,
  };

  // Determine field type from MCP type string
  const mcpType = mcpField.type.toLowerCase();

  // Handle nested schema (group)
  if (mcpField.nested_schema) {
    const groupField: GroupField = {
      ...baseField,
      type: "group",
      fields: mcpField.nested_schema.fields.map(convertField),
      is_list: mcpField.is_list,
      columns: mcpField.columns,
      field_direction: mcpField.field_direction,
      collapsible: mcpField.collapsible ?? true,
      collapsed: mcpField.collapsed ?? false,
    };
    return groupField;
  }

  // Handle object type (implicit group)
  if (mcpType === "object" || mcpType.includes("object")) {
    const groupField: GroupField = {
      ...baseField,
      type: "group",
      fields: [],
      collapsible: true,
      collapsed: false,
    };
    return groupField;
  }

  // Handle info type (display-only)
  if (mcpType === "info") {
    return {
      ...baseField,
      type: "info" as const,
    };
  }

  // Handle table_select type
  if (mcpType === "table_select") {
    const mcpTableField = mcpField as unknown as {
      columns?: { key: string; label: string; sortable?: boolean }[];
      data?: Record<string, unknown>[];
      selection_mode?: "single" | "multi";
      default_selected?: string | string[];
      searchable?: boolean;
      search_placeholder?: string;
      search_fields?: string[];
      pagination?: { enabled?: boolean; page_size?: number };
      max_height?: string;
    };

    // Ensure data rows have id field - use first column as id if not present
    const columns = mcpTableField.columns || [];
    const idColumn = columns.length > 0 ? columns[0].key : "id";
    const data = (mcpTableField.data || []).map((row, index) => ({
      id: row.id !== undefined ? String(row.id) : (row[idColumn] !== undefined ? String(row[idColumn]) : String(index)),
      ...row,
    }));

    // Handle default_selected: "all"
    let defaultValue: string | string[] | undefined;
    if (mcpTableField.default_selected === "all") {
      defaultValue = data.map((row) => row.id);
    } else if (mcpTableField.default_selected) {
      defaultValue = mcpTableField.default_selected;
    }

    return {
      ...baseField,
      type: "table_select" as const,
      columns: columns,
      data: data,
      selection_mode: mcpTableField.selection_mode || "multi",
      value: defaultValue,
      searchable: mcpTableField.searchable ?? true,
      search_placeholder: mcpTableField.search_placeholder,
      search_fields: mcpTableField.search_fields,
      pagination: mcpTableField.pagination,
      max_height: mcpTableField.max_height || "400px",
    };
  }

  // Handle dropdown type explicitly
  if (mcpType === "dropdown") {
    // Options can be string[] or DropdownOption[]
    let options: DropdownOption[] = [];
    if (mcpField.options && mcpField.options.length > 0) {
      // Check if first option is string or object
      const first = mcpField.options[0];
      if (typeof first === "string") {
        // Convert string array to DropdownOption[]
        options = (mcpField.options as unknown as string[]).map((v) => ({ label: v, value: v }));
      } else {
        // Already DropdownOption[]
        options = mcpField.options as DropdownOption[];
      }
    } else if (mcpField.allowed_values && mcpField.allowed_values.length > 0) {
      options = mcpField.allowed_values.map((v) => ({ label: v, value: v }));
    }
    return {
      ...baseField,
      type: "dropdown",
      options,
    };
  }

  // Handle allowed_values (dropdown)
  if (mcpField.allowed_values && mcpField.allowed_values.length > 0) {
    return {
      ...baseField,
      type: "dropdown",
      options: mcpField.allowed_values.map((v) => ({ label: v, value: v })),
    };
  }

  // Handle explicit options (dropdown) - options could be string[] or DropdownOption[]
  if (mcpField.options && mcpField.options.length > 0) {
    const first = mcpField.options[0];
    const options: DropdownOption[] = typeof first === "string"
      ? (mcpField.options as unknown as string[]).map((v) => ({ label: v, value: v }))
      : (mcpField.options as DropdownOption[]);
    return {
      ...baseField,
      type: "dropdown",
      options,
    };
  }

  // Handle sensitive/secret fields
  if (mcpField.sensitive || mcpType.includes("secret") || mcpType.includes("password")) {
    return {
      ...baseField,
      type: "secret",
    };
  }

  // Handle code fields
  if (mcpType === "code" || mcpField.language) {
    return {
      ...baseField,
      type: "code",
      language: mcpField.language || "json",
    };
  }

  // Handle integer
  if (mcpType === "int" || mcpType === "integer" || mcpType.includes("int")) {
    return {
      ...baseField,
      type: "integer",
      range_spec: mcpField.range
        ? {
            min: mcpField.range.min,
            max: mcpField.range.max,
            step: mcpField.range.step ?? 1,
            step_type: "int",
          }
        : undefined,
    };
  }

  // Handle float/number
  if (mcpType === "float" || mcpType === "number" || mcpType.includes("float")) {
    return {
      ...baseField,
      type: "float",
      range_spec: mcpField.range
        ? {
            min: mcpField.range.min,
            max: mcpField.range.max,
            step: mcpField.range.step ?? 0.1,
            step_type: "float",
          }
        : undefined,
    };
  }

  // Handle boolean
  if (mcpType === "bool" || mcpType === "boolean") {
    return {
      ...baseField,
      type: "boolean",
    };
  }

  // Handle array (list of values) - nested_schema arrays already handled above
  if (mcpType.includes("array") || mcpType.includes("list")) {
    // Simple array of strings - treat as multi-line text input
    return {
      ...baseField,
      type: "string",
      multiline: true,
    };
  }

  // Handle dict/map type
  if (mcpType === "dict" || mcpType.includes("dict") || mcpType.includes("map")) {
    return {
      ...baseField,
      type: "dict",
      is_list: mcpField.is_list ?? true,
      key_field: {
        name: "key",
        display_name: "Key",
        type: "string",
      },
      value_field: {
        name: "value",
        display_name: "Value",
        type: "string",
      },
    };
  }

  // Default to string
  return {
    ...baseField,
    type: "string",
    multiline: mcpField.multiline,
  };
}

/**
 * Format field name to display name
 * "api_key" -> "Api Key"
 */
function formatDisplayName(name: string): string {
  return name
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Helper to build form values from schema defaults
 * Checks field.value first (for pre-set values), then field.default
 */
export function buildDefaultValues(schema: ResourceSchema): Record<string, unknown> {
  const values: Record<string, unknown> = {};

  const processFields = (fields: ResourceField[], target: Record<string, unknown>) => {
    for (const field of fields) {
      // Check for pre-set value first (e.g., table_select with default_selected: "all")
      if (field.value !== undefined) {
        target[field.name] = field.value;
      } else if (field.default !== undefined) {
        target[field.name] = field.default;
      } else if (field.type === "group" && "fields" in field) {
        if (field.is_list) {
          target[field.name] = [{}];
        } else {
          target[field.name] = {};
          processFields(field.fields, target[field.name] as Record<string, unknown>);
        }
      } else if (field.type === "boolean") {
        target[field.name] = false;
      }
    }
  };

  processFields(schema.fields, values);
  return values;
}

/**
 * Helper to extract values for API submission
 * Removes undefined and empty values
 */
export function cleanFormValues(values: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null || value === "") {
      continue;
    }

    if (Array.isArray(value)) {
      const cleanedArray = value
        .map((item) =>
          typeof item === "object" && item !== null
            ? cleanFormValues(item as Record<string, unknown>)
            : item
        )
        .filter((item) => {
          if (typeof item === "object" && item !== null) {
            return Object.keys(item).length > 0;
          }
          return item !== undefined && item !== null && item !== "";
        });

      if (cleanedArray.length > 0) {
        cleaned[key] = cleanedArray;
      }
    } else if (typeof value === "object" && value !== null) {
      const cleanedObj = cleanFormValues(value as Record<string, unknown>);
      if (Object.keys(cleanedObj).length > 0) {
        cleaned[key] = cleanedObj;
      }
    } else {
      cleaned[key] = value;
    }
  }

  return cleaned;
}
